/**
 * Reordering a list by dragging, and remembering the result.
 *
 * Dexie-free and DOM-free on purpose. The gesture code around this is the part
 * that can only really be judged with a thumb on a phone; the arithmetic — which
 * slot a finger is over, what the list becomes, how a saved order survives the
 * plan changing underneath it — does not need a screen to be checked, so it
 * lives here where it can be.
 */

/** Move one item, returning a new list. Out-of-range or no-op returns the same. */
export function moveItem<T>(list: T[], from: number, to: number): T[] {
  if (from === to) return list
  if (from < 0 || from >= list.length) return list
  if (to < 0 || to >= list.length) return list
  const next = [...list]
  const [item] = next.splice(from, 1)
  next.splice(to, 0, item)
  return next
}

/**
 * Where a pointer sits as an *insertion* position, 0..n.
 *
 * Midpoints are measured once when the drag starts and not again: the list is
 * held still while a row is dragged over it, so they stay valid, and re-measuring
 * mid-gesture against rows that are themselves moving is how drag lists end up
 * flickering between two slots.
 */
export function insertionIndex(pointerY: number, midpoints: number[]): number {
  let i = 0
  while (i < midpoints.length && pointerY > midpoints[i]) i++
  return i
}

/**
 * Where the dragged row would land, as a final index in the reordered list.
 *
 * An insertion position past the row's own slot has to lose one, because the row
 * is removed before it is reinserted. Getting this wrong is the classic
 * off-by-one where dragging down one place appears to do nothing.
 */
export function targetIndex(pointerY: number, midpoints: number[], from: number): number {
  const at = insertionIndex(pointerY, midpoints)
  return at > from ? at - 1 : at
}

/**
 * Put `items` into a remembered order.
 *
 * Anything the order doesn't mention keeps its relative sequence and goes last.
 * That covers a plan edited mid-session — a new exercise appears at the bottom of
 * today's queue rather than at a position nobody chose — and it covers an item
 * whose exercise the server couldn't resolve, which has no id to be ranked by and
 * so drifts to the end once any reorder has happened. Both are rare, and both
 * are better than a list whose order depends on which happened first.
 */
export function applyOrder<T>(
  items: T[],
  idOf: (item: T) => number | null | undefined,
  order: number[],
): T[] {
  if (order.length === 0) return items
  const rank = new Map(order.map((id, i) => [id, i]))

  const ranked: { item: T; at: number }[] = []
  const rest: T[] = []
  for (const item of items) {
    const id = idOf(item)
    const at = id === null || id === undefined ? undefined : rank.get(id)
    if (at === undefined) rest.push(item)
    else ranked.push({ item, at })
  }
  ranked.sort((a, b) => a.at - b.at)
  return [...ranked.map((r) => r.item), ...rest]
}

/**
 * How fast to scroll the page, in pixels per frame, while a row is held near an
 * edge of the screen.
 *
 * Without this a drag can only reach what was already visible when it started.
 * Everything below the fold is unreachable: the row travels to the bottom of the
 * window and stops, because the drag deliberately blocks the page from scrolling
 * so it doesn't fight the gesture. On a six-exercise queue with the rest timer
 * pinned above it, "below the fold" is most of the list.
 *
 * Proportional to depth into the zone rather than a fixed speed, so easing into
 * the edge creeps and pushing right to it moves properly — one gesture covers
 * both "one place further" and "all the way to the end".
 *
 * Positive scrolls down. Zero in the middle, which is most of the screen.
 */
export function edgeScrollVelocity(
  clientY: number,
  viewportHeight: number,
  opts: { zone?: number; max?: number } = {},
): number {
  const zone = opts.zone ?? 96
  const max = opts.max ?? 18
  if (viewportHeight <= 0 || zone <= 0) return 0

  // A viewport too short for two zones would otherwise have them overlap and
  // fight in the middle. Shrink both instead of picking a winner.
  const band = Math.min(zone, viewportHeight / 2)

  if (clientY < band) {
    const depth = Math.min(1, (band - clientY) / band)
    return -Math.max(1, Math.round(depth * max))
  }
  const fromBottom = viewportHeight - clientY
  if (fromBottom < band) {
    const depth = Math.min(1, (band - fromBottom) / band)
    return Math.max(1, Math.round(depth * max))
  }
  return 0
}

/**
 * Fold one list's new order into the session's saved order.
 *
 * A workout has two independently ordered lists — the plan's exercises and the
 * ones added today — and they are disjoint, so one array can carry both:
 * `applyOrder` only ever looks at the ids in the list it was handed. Reordering
 * one list therefore must not disturb what the array says about the other, which
 * is all this does.
 */
export function mergeOrder(saved: number[], reordered: number[]): number[] {
  const moved = new Set(reordered)
  return [...reordered, ...saved.filter((id) => !moved.has(id))]
}
